import { useLazyGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberPdfQuery } from "randmar-api-client";
import { Button, useToast } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Download, Loader2, Printer } from "lucide-react";
import React from "react";

interface IProps {
  documentNumber: string;
  disabled?: boolean;
  stealth?: boolean;
}

const OpenPDFButton: React.FC<IProps> = ({
  documentNumber,
  disabled,
  stealth,
}) => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const [trigger, { isFetching }] =
    useLazyGetV4ManufacturerByRouteManufacturerIdDocumentAndDocumentNumberPdfQuery();

  const [activeAction, setActiveAction] = React.useState<
    "print" | "download" | null
  >(null);

  React.useEffect(() => {
    if (!isFetching) {
      setActiveAction(null);
    }
  }, [isFetching]);

  const handlePrint = async () => {
    setActiveAction("print");
    try {
      const response = await trigger({
        routeManufacturerId: applicationId,
        documentNumber,
      }).unwrap();

      const file = new Blob([response as any], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (error) {
      console.error("Failed to print PDF", error);
      toast({
        variant: "destructive",
        title: "Error printing document",
        description:
          "There was a problem generating the PDF for printing. Please try again.",
      });
    }
  };

  const handleDownload = async () => {
    setActiveAction("download");
    try {
      const response = await trigger({
        routeManufacturerId: applicationId,
        documentNumber,
      }).unwrap();

      const file = new Blob([response as any], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);

      const fileLink = document.createElement("a");
      fileLink.href = fileURL;
      fileLink.target = "_blank";
      fileLink.download = `${documentNumber}.pdf`;
      fileLink.click();
    } catch (error) {
      console.error("Failed to download PDF", error);
      toast({
        variant: "destructive",
        title: "Error downloading document",
        description:
          "There was a problem generating the PDF for download. Please try again.",
      });
    }
  };

  if (stealth) {
    return (
      <Button
        title={"Open " + documentNumber + " in new window"}
        disabled={disabled || isFetching}
        onClick={handlePrint}
      >
        {isFetching && activeAction === "print" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Printer className="mr-2 h-4 w-4" />
        )}
        Print Document
      </Button>
    );
  }

  return (
    <>
      <Button
        title={"Download " + documentNumber}
        disabled={disabled || isFetching}
        onClick={handleDownload}
        className="mr-2"
        size="icon"
      >
        {isFetching && activeAction === "download" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
      </Button>

      <Button
        title={"Open " + documentNumber + " in new window"}
        disabled={disabled || isFetching}
        onClick={handlePrint}
      >
        {isFetching && activeAction === "print" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Printer className="mr-2 h-4 w-4" />
        )}
        Print Document
      </Button>
    </>
  );
};

export default OpenPDFButton;
